import {useEffect, useRef, useState} from 'react'
import {useAccessToken, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useDeliveryOptions, GEOLOCATION_KEY_PREFIX} from '../integrations/delivery-options/hooks/use-delivery-options'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

/**
 * Hook that resolves geolocation from sessionStorage (or by calling delivery-options API)
 * and submits a partial shipping address (countryCode + postalCode) to the basket
 * so the backend can calculate estimated taxes and shipping costs.
 *
 * The partial address uses address1='basketOnly' to signal it is not a real address,
 * which prevents it from appearing in shipping form fields.
 */
const useGeoEstimation = () => {
    const {site} = useMultiSite()
    const geolocationKey = `${GEOLOCATION_KEY_PREFIX}${site.id}`
    const {data: basket} = useCurrentBasket()
    const {getTokenWhenReady} = useAccessToken()
    const [token, setToken] = useState(null)
    const hasSubmitted = useRef(false)

    // Fetch access token on mount
    useEffect(() => {
        let cancelled = false
        getTokenWhenReady()
            .then((t) => {
                if (!cancelled) setToken(t)
            })
            .catch((err) => {
                logger.error('useGeoEstimation: failed to get access token', {
                    namespace: 'GeoEstimation',
                    additionalProperties: {error: err.message}
                })
            })
        return () => {
            cancelled = true
        }
    }, [])

    // Use the existing delivery-options hook (it will read from cache if PDP already fetched)
    const {deliveryOptionResponse, isLoading: isDeliveryLoading} = useDeliveryOptions({
        accessToken: token,
        cache: true
    })

    const updateShippingAddress = useShopperBasketsMutation('updateShippingAddressForShipment')

    useEffect(() => {
        if (hasSubmitted.current) return
        if (!basket?.basketId) return

        // If the basket already has a real shipping address, don't overwrite it
        const existingAddress = basket?.shipments?.[0]?.shippingAddress
        if (existingAddress?.address1 && existingAddress.address1 !== 'basketOnly') return

        // Try to read geolocation from sessionStorage
        let geo = null
        try {
            const stored = sessionStorage.getItem(geolocationKey)
            if (stored) {
                geo = JSON.parse(stored)
            }
        } catch {
            // ignore parse errors
        }

        // If delivery-options response just came back with geolocation, extract it
        if (!geo && deliveryOptionResponse?.geolocation) {
            const {PostalCode, CountryCode} = deliveryOptionResponse.geolocation
            if (PostalCode && CountryCode) {
                geo = {postalCode: String(PostalCode), countryCode: CountryCode}
            }
        }

        if (!geo?.postalCode || !geo?.countryCode) return

        hasSubmitted.current = true

        updateShippingAddress.mutate(
            {
                parameters: {
                    basketId: basket.basketId,
                    shipmentId: 'me',
                    useAsBilling: false
                },
                body: {
                    address1: 'basketOnly',
                    countryCode: geo.countryCode,
                    postalCode: geo.postalCode
                }
            },
            {
                onError: (error) => {
                    hasSubmitted.current = false
                    logger.error('useGeoEstimation: failed to submit partial address', {
                        namespace: 'GeoEstimation',
                        additionalProperties: {error: error.message}
                    })
                }
            }
        )
    }, [basket?.basketId, basket?.shipments, deliveryOptionResponse, isDeliveryLoading])
}

export default useGeoEstimation
